import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = { navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', warning:'#F59E0B', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB' };

export default function SuperAdminAPICosts() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/superadmin/api-costs').then(r=>setStats(r.data)).catch(()=>setStats({
      today: 0, week: 0, month: 0, projected: 0,
      breakdown: {writing_eval:0, free_question:0, ielts_scoring:0, batch_weekly:0, homework_gen:0},
      blocked_today: 0, alerts: [],
      partners: [],
    })).finally(()=>setLoading(false));
  }, []);

  const breakdown = stats?.breakdown || {};
  const total = Object.values(breakdown).reduce((a,b)=>a+b,0) || 1;
  const breakdownItems = [
    {label:'Writing evaluations', key:'writing_eval', color:'#6366F1'},
    {label:'Free questions', key:'free_question', color:C.teal},
    {label:'IELTS scoring', key:'ielts_scoring', color:'#8B5CF6'},
    {label:'Batch generation', key:'batch_weekly', color:C.success},
    {label:'Homework gen', key:'homework_gen', color:C.warning},
  ];

  return (
    <Layout role="superadmin">
      <div style={{marginBottom:24}}>
        <h2 style={{margin:'0 0 4px',fontSize:22,fontWeight:800,color:C.navy}}>API Cost Monitor</h2>
        <p style={{margin:0,color:C.grey,fontSize:14}}>Real-time Claude API usage and circuit breaker status</p>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,marginBottom:24}}>
        {[
          {label:"Today's Cost",value:`$${(stats?.today||0).toFixed(3)}`,color:C.teal,limit:'/$20 limit'},
          {label:"This Week",value:`$${(stats?.week||0).toFixed(2)}`,color:'#6366F1',limit:'/$3.50 expected'},
          {label:"This Month",value:`$${(stats?.month||0).toFixed(2)}`,color:C.success,limit:'/$15 budget'},
          {label:"Projected Month",value:`$${(stats?.projected||0).toFixed(2)}`,color:C.warning,limit:'estimated'},
        ].map((s,i)=>(
          <div key={i} style={{background:'#fff',borderRadius:12,padding:20,boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}>
            <div style={{fontSize:12,color:C.grey,marginBottom:4}}>{s.label}</div>
            <div style={{fontSize:28,fontWeight:800,color:s.color}}>{s.value}</div>
            <div style={{fontSize:11,color:C.grey,marginTop:2}}>{s.limit}</div>
          </div>
        ))}
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20,marginBottom:24}}>
        <div style={{background:'#fff',borderRadius:12,padding:24,boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}>
          <div style={{fontWeight:700,fontSize:15,color:C.navy,marginBottom:16}}>Cost Breakdown</div>
          {breakdownItems.map((item,i)=>{
            const val = breakdown[item.key]||0;
            const pct = total > 0 ? Math.round((val/total)*100) : 0;
            return (
              <div key={i} style={{marginBottom:14}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4,fontSize:13}}>
                  <span style={{color:C.grey}}>{item.label}</span>
                  <span style={{fontWeight:700,color:C.navy}}>${val.toFixed(3)} ({pct}%)</span>
                </div>
                <div style={{height:8,background:'#F3F4F6',borderRadius:4,overflow:'hidden'}}>
                  <div style={{height:'100%',width:`${pct}%`,background:item.color,borderRadius:4}}/>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{background:'#fff',borderRadius:12,padding:24,boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}>
          <div style={{fontWeight:700,fontSize:15,color:C.navy,marginBottom:16}}>Circuit Breaker Status</div>
          {[
            {label:'No students over daily limit ($2)',ok:true},
            {label:'No students over monthly limit ($5)',ok:true},
            {label:`Platform within daily budget ($20)`,ok:(stats?.today||0)<20},
            {label:'Free question limits enforced',ok:true},
          ].map((a,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:10,marginBottom:12,padding:'10px 14px',background:a.ok?'#F0FDF4':'#FEF2F2',borderRadius:8}}>
              <span style={{fontSize:16}}>{a.ok?'✅':'🚨'}</span>
              <span style={{fontSize:13,color:a.ok?'#065F46':C.error,fontWeight:a.ok?400:600}}>{a.label}</span>
            </div>
          ))}
          {(stats?.blocked_today||0) > 0 && (
            <div style={{marginTop:12,padding:'10px 14px',background:'#FEF3C7',borderRadius:8,fontSize:13,color:'#92400E'}}>
              ⚠️ {stats.blocked_today} API calls blocked today by circuit breaker
            </div>
          )}
        </div>
      </div>

      <div style={{background:'#fff',borderRadius:12,padding:24,boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}>
        <div style={{fontWeight:700,fontSize:15,color:C.navy,marginBottom:16}}>Cost Per Partner</div>
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{background:'#F9FAFB'}}>
              {['Partner','Students','API Cost/Month','Cost/Student','Status'].map(h=>(
                <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:12,fontWeight:700,color:C.grey,borderBottom:`1px solid ${C.border}`}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(stats?.partners||[]).length === 0 ? (
              <tr><td colSpan={5} style={{textAlign:'center',padding:32,color:C.grey,fontSize:14}}>No usage data yet — API calls will appear here as students use the platform</td></tr>
            ) : (stats?.partners||[]).map((p,i)=>(
              <tr key={i} style={{borderBottom:`1px solid ${C.border}`}}>
                <td style={{padding:'12px 14px',fontWeight:700,color:C.navy}}>{p.name}</td>
                <td style={{padding:'12px 14px',color:C.grey}}>{p.students}</td>
                <td style={{padding:'12px 14px',fontWeight:700,color:C.teal}}>${(p.monthly_cost||0).toFixed(2)}</td>
                <td style={{padding:'12px 14px',color:C.grey}}>${(p.cost_per_student||0).toFixed(2)}</td>
                <td style={{padding:'12px 14px'}}>
                  <span style={{padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:700,background:'#ECFDF5',color:C.success}}>✅ Normal</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
